import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { digitalArtworks, type DigitalArtwork } from "../data/digitalArtworks";
import {
  canvasArtworks,
  type CanvasArtwork,
} from "../data/canvasArtworks";
import {
  canvasCollectionCopy,
  canvasCollectionFilterIds,
  type CanvasCollectionFilterId,
} from "../data/canvasArtworkCatalog";
import { aiArtworks } from "../data/aiArtworks";
import {
  aiCollectionCopy,
  aiCollectionFilterIds,
  compareAiArtworksForAllView,
  groupAiArtworksForAllView,
  type AiCollectionFilterId,
} from "../data/aiArtworkCatalog";
import {
  digitalCollectionCopy,
  digitalCollectionFilterIds,
  type DigitalCollectionFilterId,
} from "../data/digitalArtworkCatalog";
import { CanvasGalleryCard } from "./CanvasArtworkCards";
import { DigitalGalleryCard } from "./DigitalArtworkCards";
import { AiGalleryCard } from "./AiArtworkCards";
import { CanvasLightbox } from "./CanvasLightbox";
import { AiLightbox } from "./AiLightbox";
import { DigitalLightbox } from "./DigitalLightbox";

type GalleryTab = "canvas" | "digital" | "ai";

const VALID_TABS: GalleryTab[] = ["canvas", "digital", "ai"];

function parseTab(tab: string | null): GalleryTab {
  if (tab === "themes") return "canvas";
  if (tab && VALID_TABS.includes(tab as GalleryTab)) {
    return tab as GalleryTab;
  }
  return "canvas";
}

function parseAiCollection(value: string | null): AiCollectionFilterId {
  if (value && aiCollectionFilterIds.includes(value as AiCollectionFilterId)) {
    return value as AiCollectionFilterId;
  }
  return "all";
}

function parseDigitalCollection(
  value: string | null,
): DigitalCollectionFilterId {
  if (
    value &&
    digitalCollectionFilterIds.includes(value as DigitalCollectionFilterId)
  ) {
    return value as DigitalCollectionFilterId;
  }
  return "all";
}

function parseCanvasCollection(
  value: string | null,
): CanvasCollectionFilterId {
  if (
    value &&
    canvasCollectionFilterIds.includes(value as CanvasCollectionFilterId)
  ) {
    return value as CanvasCollectionFilterId;
  }
  return "all";
}

function collectionParamForTab(
  tab: GalleryTab,
  value: string | null,
): string | null {
  if (!value || value === "all") return null;
  if (tab === "ai" && aiCollectionFilterIds.includes(value as AiCollectionFilterId)) {
    return value;
  }
  if (
    tab === "digital" &&
    digitalCollectionFilterIds.includes(value as DigitalCollectionFilterId)
  ) {
    return value;
  }
  return null;
}

export default function Gallery() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("he") ? "he" : "en";
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const collectionParam = searchParams.get("collection");
  const [activeTab, setActiveTab] = useState<GalleryTab>(parseTab(tabParam));
  const [aiCollection, setAiCollection] = useState<AiCollectionFilterId>(
    parseAiCollection(collectionParam),
  );
  const [digitalCollection, setDigitalCollection] =
    useState<DigitalCollectionFilterId>(parseDigitalCollection(collectionParam));
  const [canvasCollection, setCanvasCollection] =
    useState<CanvasCollectionFilterId>(parseCanvasCollection(collectionParam));
  const [canvasLightbox, setCanvasLightbox] = useState<CanvasArtwork | null>(null);
  const [aiLightbox, setAiLightbox] = useState<(typeof aiArtworks)[number] | null>(null);
  const [digitalLightbox, setDigitalLightbox] = useState<DigitalArtwork | null>(null);

  useEffect(() => {
    const tab = parseTab(tabParam);
    setActiveTab(tab);
    setAiCollection(parseAiCollection(collectionParam));
    setDigitalCollection(parseDigitalCollection(collectionParam));
    setCanvasCollection(parseCanvasCollection(collectionParam));
  }, [tabParam, collectionParam]);

  const switchTab = (tab: GalleryTab) => {
    setActiveTab(tab);
    const collection = collectionParamForTab(tab, collectionParam);
    if (collection) {
      setSearchParams({ tab, collection });
    } else {
      setSearchParams({ tab });
    }
    setCanvasLightbox(null);
    setAiLightbox(null);
    setDigitalLightbox(null);
  };

  const switchAiCollection = (collectionId: AiCollectionFilterId) => {
    setAiCollection(collectionId);
    if (collectionId === "all") {
      setSearchParams({ tab: "ai" });
    } else {
      setSearchParams({ tab: "ai", collection: collectionId });
    }
  };

  const switchDigitalCollection = (collectionId: DigitalCollectionFilterId) => {
    setDigitalCollection(collectionId);
    if (collectionId === "all") {
      setSearchParams({ tab: "digital" });
    } else {
      setSearchParams({ tab: "digital", collection: collectionId });
    }
  };

  const switchCanvasCollection = (collectionId: CanvasCollectionFilterId) => {
    setCanvasCollection(collectionId);
    if (collectionId === "all") {
      setSearchParams({ tab: "canvas" });
    } else {
      setSearchParams({ tab: "canvas", collection: collectionId });
    }
  };

  const filteredCanvas =
    canvasCollection === "all"
      ? canvasArtworks
      : canvasArtworks.filter((w) => w.collection === canvasCollection);

  const filteredAi =
    aiCollection === "all"
      ? [...aiArtworks].sort(compareAiArtworksForAllView)
      : aiArtworks.filter((w) => w.collection === aiCollection);

  const aiAllGroups = useMemo(
    () =>
      aiCollection === "all"
        ? groupAiArtworksForAllView(filteredAi)
        : null,
    [aiCollection, filteredAi],
  );

  const filteredDigital =
    digitalCollection === "all"
      ? digitalArtworks
      : digitalArtworks.filter((w) => w.collection === digitalCollection);

  const closeCanvasLightbox = useCallback(() => setCanvasLightbox(null), []);
  const closeAiLightbox = useCallback(() => setAiLightbox(null), []);

  const navigateCanvasLightbox = useCallback(
    (direction: -1 | 1) => {
      if (!canvasLightbox) return;
      const list = filteredCanvas;
      const currentIndex = list.findIndex((w) => w.id === canvasLightbox.id);
      const nextIndex = (currentIndex + direction + list.length) % list.length;
      setCanvasLightbox(list[nextIndex]);
    },
    [canvasLightbox, filteredCanvas],
  );

  const navigateAiLightbox = useCallback(
    (direction: -1 | 1) => {
      if (!aiLightbox) return;
      const list = filteredAi;
      const currentIndex = list.findIndex((w) => w.id === aiLightbox.id);
      const nextIndex = (currentIndex + direction + list.length) % list.length;
      setAiLightbox(list[nextIndex]);
    },
    [aiLightbox, filteredAi],
  );

  const navigateDigitalLightbox = useCallback(
    (direction: -1 | 1) => {
      if (!digitalLightbox) return;
      const list = filteredDigital;
      const currentIndex = list.findIndex((w) => w.id === digitalLightbox.id);
      const nextIndex = (currentIndex + direction + list.length) % list.length;
      setDigitalLightbox(list[nextIndex]);
    },
    [digitalLightbox, filteredDigital],
  );

  return (
    <main>
      <section className="section-container py-10 sm:py-14">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-medium uppercase tracking-[0.15em] text-accent">
            {t("gallery.eyebrow")}
          </p>
          <h1 className="mt-2 font-display text-4xl font-semibold text-canvas-950 sm:text-5xl">
            {t("gallery.title")}
          </h1>
          <p className="mt-3 text-base text-canvas-600">{t("gallery.description")}</p>
        </div>

        <div className="mx-auto mt-8 flex max-w-md justify-center rounded-full bg-canvas-200/50 p-1">
          {VALID_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => switchTab(tab)}
              className={`flex-1 rounded-full px-4 py-2.5 text-sm font-medium transition-all duration-300 ${
                activeTab === tab
                  ? "bg-white text-canvas-950 shadow-sm"
                  : "text-canvas-600 hover:text-canvas-900"
              }`}
            >
              {t(`gallery.tabs.${tab}`)}
            </button>
          ))}
        </div>
      </section>

      {activeTab === "canvas" && (
        <section className="section-container pb-16 sm:pb-20">
          <div className="mb-6 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1 scrollbar-none">
            {canvasCollectionFilterIds.map((collectionId) => (
              <button
                key={collectionId}
                type="button"
                onClick={() => switchCanvasCollection(collectionId)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition ${
                  canvasCollection === collectionId
                    ? "bg-canvas-900 text-white"
                    : "bg-canvas-100 text-canvas-700 hover:bg-canvas-200"
                }`}
              >
                {collectionId === "all"
                  ? t("canvas.categories.all")
                  : canvasCollectionCopy[collectionId][lang]}
              </button>
            ))}
          </div>

          <div className="columns-1 gap-5 sm:columns-2 lg:columns-3">
            {filteredCanvas.map((work, i) => (
              <CanvasGalleryCard
                key={work.id}
                work={work}
                onClick={() => setCanvasLightbox(work)}
                style={{ animationDelay: `${i * 40}ms` }}
              />
            ))}
          </div>

          <GalleryCta />
        </section>
      )}

      {activeTab === "digital" && (
        <section className="section-container pb-16 sm:pb-20">
          <div className="mb-6 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1 scrollbar-none">
            {digitalCollectionFilterIds.map((collectionId) => (
              <button
                key={collectionId}
                type="button"
                onClick={() => switchDigitalCollection(collectionId)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition ${
                  digitalCollection === collectionId
                    ? "bg-canvas-900 text-white"
                    : "bg-canvas-100 text-canvas-700 hover:bg-canvas-200"
                }`}
              >
                {collectionId === "all"
                  ? t("digital.collections.all")
                  : digitalCollectionCopy[collectionId][lang]}
              </button>
            ))}
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredDigital.map((work) => (
              <DigitalGalleryCard
                key={work.id}
                work={work}
                onClick={() => setDigitalLightbox(work)}
              />
            ))}
          </div>
          <GalleryCta />
        </section>
      )}

      {activeTab === "ai" && (
        <section className="section-container pb-16 sm:pb-20">
          <div className="mb-6 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1 scrollbar-none">
            {aiCollectionFilterIds.map((collectionId) => (
              <button
                key={collectionId}
                type="button"
                onClick={() => switchAiCollection(collectionId)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition ${
                  aiCollection === collectionId
                    ? "bg-canvas-900 text-white"
                    : "bg-canvas-100 text-canvas-700 hover:bg-canvas-200"
                }`}
              >
                {collectionId === "all"
                  ? t("ai.collections.all")
                  : aiCollectionCopy[collectionId][lang]}
              </button>
            ))}
          </div>

          {aiAllGroups ? (
            <div className="space-y-12 sm:space-y-14">
              {aiAllGroups.map(({ collectionId, works }) => (
                <div
                  key={collectionId}
                  className="columns-1 gap-5 sm:columns-2 lg:columns-3"
                >
                  {works.map((work, i) => (
                    <AiGalleryCard
                      key={work.id}
                      work={work}
                      onClick={() => setAiLightbox(work)}
                      style={{ animationDelay: `${i * 30}ms` }}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="columns-1 gap-5 sm:columns-2 lg:columns-3">
              {filteredAi.map((work, i) => (
                <AiGalleryCard
                  key={work.id}
                  work={work}
                  onClick={() => setAiLightbox(work)}
                  style={{ animationDelay: `${i * 30}ms` }}
                />
              ))}
            </div>
          )}

          <GalleryCta dark />
        </section>
      )}

      {canvasLightbox && (
        <CanvasLightbox
          work={canvasLightbox}
          onClose={closeCanvasLightbox}
          onNavigate={navigateCanvasLightbox}
        />
      )}

      {aiLightbox && (
        <AiLightbox
          work={aiLightbox}
          onClose={closeAiLightbox}
          onNavigate={navigateAiLightbox}
        />
      )}

      {digitalLightbox && (
        <DigitalLightbox
          work={digitalLightbox}
          onClose={() => setDigitalLightbox(null)}
          onNavigate={navigateDigitalLightbox}
        />
      )}
    </main>
  );
}

function GalleryCta({ dark = false }: { dark?: boolean }) {
  const { t } = useTranslation();
  return (
    <div
      className={`mt-12 rounded-2xl p-8 text-center ${
        dark ? "bg-canvas-900" : "bg-canvas-100"
      }`}
    >
      <p
        className={`font-display text-xl ${dark ? "text-white" : "text-canvas-950"}`}
      >
        {t("gallery.inquire.title")}
      </p>
      <Link
        to="/contact#commission"
        className={`mt-5 inline-flex ${dark ? "btn-accent" : "btn-primary"} !px-6 !py-2.5 !text-xs`}
      >
        {t("gallery.inquire.cta")}
      </Link>
    </div>
  );
}
